import React, { useState, useEffect, useRef } from 'react';
import { Copy, Download } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from '../axiosInstance';

export default function BranchTable() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const token = localStorage.getItem('token');

  const [showModal, setShowModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [modalError, setModalError] = useState('');
  const [newBranch, setNewBranch] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '12345678',
    mobile_no: '',
    address: '',
    stateId: '',
    cityId: '',
  });
  const [role, setRole] = useState('');

  // Fetch branches with pagination
  const fetchBranches = async (
    page = 1,
    search = '',
    state = '',
    city = ''
  ) => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      if (search) params.append('search', search);
      if (state) params.append('state', state); // State ID from dropdown
      if (city) params.append('city', city); // City ID from dropdown

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}user/branches?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // console.log('Fetched branches response:', response.data);
      // Update branches data
      setBranches(response.data.data || []);

      // Update pagination data
      setTotalPages(response.data.meta?.totalPages || 1);
      setLimit(response.data.meta?.limit || limit);
    } catch (err) {
      console.error('Error fetching branches:', err);
      setError(err.response?.data?.message || 'Failed to fetch branches.');
      setBranches([]);
    } finally {
      setLoading(false);
    }
  };

  // Add branch
  const handleAddBranch = async () => {
    // Clear previous modal errors
    setModalError('');

    // Validation checks
    if (!newBranch.first_name || !newBranch.email || !newBranch.mobile_no) {
      setModalError('Branch name, email, and mobile number are required.');
      return;
    }

    // Mobile number validation (10 digits)
    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(newBranch.mobile_no)) {
      setModalError('Mobile number must be exactly 10 digits.');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newBranch.email)) {
      setModalError('Please enter a valid email address.');
      return;
    }

    try {
      setLoading(true);

      await axios.post(
        `${process.env.REACT_APP_BASE_URL}user/add-branch`,
        newBranch,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      fetchBranches(); // Refresh the list after adding

      setNewBranch({
        first_name: '',
        last_name: '',
        email: '',
        password: '12345678',
        mobile_no: '',
        address: '',
        stateId: '',
        cityId: '',
      });

      setModalError('');
      setShowModal(false);
    } catch (err) {
      setModalError(err.response?.data?.message || 'Failed to add branch.');
    } finally {
      setLoading(false);
    }
  };

  // Update branch
  const handleUpdateBranch = async () => {
    // Clear previous modal errors
    setModalError('');

    // Validation checks
    if (!newBranch.first_name || !newBranch.email || !newBranch.mobile_no) {
      setModalError('Branch name, email, and mobile number are required.');
      return;
    }

    // Mobile number validation (10 digits)
    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(newBranch.mobile_no)) {
      setModalError('Mobile number must be exactly 10 digits.');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newBranch.email)) {
      setModalError('Please enter a valid email address.');
      return;
    }

    try {
      setLoading(true);

      // Create update data without password if it's the default password
      // This prevents the backend from thinking we're trying to change the branch name
      const { password, ...dataWithoutPassword } = newBranch;

      // Include the ID in the payload for update
      const updateData = {
        ...dataWithoutPassword,
        id: editingBranch.id,
      };

      // console.log('Updating branch with data:', updateData);

      const response = await axios.patch(
        `${process.env.REACT_APP_BASE_URL}user/update-branch/${editingBranch.id}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // console.log('Update response:', response.data);

      fetchBranches(); // Refresh the list after updating

      setNewBranch({
        first_name: '',
        last_name: '',
        email: '',
        password: '12345678',
        mobile_no: '',
        address: '',
        stateId: '',
        cityId: '',
      });

      setModalError('');
      setEditingBranch(null);
      setShowModal(false);
    } catch (err) {
      console.error('Update error:', err.response?.data || err.message);
      setModalError(err.response?.data?.message || 'Failed to update branch.');
    } finally {
      setLoading(false);
    }
  };


  const handleExport = async () => {
    try {
      // Build URL with filters
      let url = `user/excel/download?`;

      if (filters.state) url += `state=${filters.state}&`;
      if (filters.city) url += `city=${filters.city}&`;
      if (filters.search) url += `search=${encodeURIComponent(filters.search)}&`;

      // Remove trailing "&" or "?" if present
      url = url.replace(/[&?]$/, '');

      const res = await axios.get(url, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Create downloadable Excel blob with Unix timestamp
      const blob = new Blob([res.data], { type: 'application/vnd.ms-excel' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', `branches_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Export error:', err.response?.data || err.message);
      alert('❌ Failed to export branch data');
    }
  };

  const [filters, setFilters] = useState({
    search: '',
    state: '',
    city: '',
  });

  // For filter dropdowns
  const [filterStates, setFilterStates] = useState([]);
  const [filterCities, setFilterCities] = useState([]);
  const [loadingFilterStates, setLoadingFilterStates] = useState(false);
  const [loadingFilterCities, setLoadingFilterCities] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);

  // States are now fetched in the useEffect hook with a single API call

  // Fetch cities based on selected state
  const fetchCities = async (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }

    try {
      setLoadingCities(true);
      const res = await axios.get(`cities/state/${stateId}`);
      setCities(res.data || []);
    } catch (err) {
      console.error('Error fetching cities:', err);
    } finally {
      setLoadingCities(false);
    }
  };

  // Handle state change in form
  const handleStateChange = (e) => {
    const stateId = e.target.value;
    setNewBranch({
      ...newBranch,
      stateId: stateId,
      cityId: '', // Reset city when state changes
    });

    fetchCities(stateId);
  };

  // We no longer need a separate refresh function as the useEffect handles this

  // Ref to track if initial data has been fetched
  const initialDataFetchedRef = useRef(false);

  // Ref to prevent duplicate API calls when filters change
  const fetchDataRef = useRef(null);

  // Fetch states on mount for both form and filters - only once
  useEffect(() => {
    if (!initialDataFetchedRef.current) {
      const user = JSON.parse(localStorage.getItem('user'));
      const userRole = user?.roles?.[0]?.name || '';
      setRole(userRole);

      // Use a single API call and set both state arrays
      const getStates = async () => {
        try {
          setLoadingStates(true);
          setLoadingFilterStates(true);
          const response = await axios.get(
            `${process.env.REACT_APP_BASE_URL}states`
          );
          const statesData = response.data || [];
          setStates(statesData);
          setFilterStates(statesData);
        } catch (err) {
          console.error('Error fetching states:', err);
        } finally {
          setLoadingStates(false);
          setLoadingFilterStates(false);
        }
      };

      getStates();
      initialDataFetchedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to fetch branches when page changes or filters change
  useEffect(() => {
    // Clear any existing timeout to prevent duplicate calls
    if (fetchDataRef.current) {
      clearTimeout(fetchDataRef.current);
    }

    // Set a timeout to debounce the API call
    fetchDataRef.current = setTimeout(() => {
      fetchBranches(currentPage, filters.search, filters.state, filters.city);
    }, 300);

    // Cleanup function to clear the timeout if component unmounts or dependencies change
    return () => {
      if (fetchDataRef.current) {
        clearTimeout(fetchDataRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters.search, filters.state, filters.city]);

  // No longer need debounce for filters as we're using useEffect dependencies

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };

    // If state filter changes, reset city filter and fetch cities for that state
    if (key === 'state' && value) {
      newFilters.city = '';
      fetchFilterCities(value);
    }

    setFilters(newFilters);

    // Reset to first page when filters change
    setCurrentPage(1);
  };

  // States for filters are now fetched in the useEffect hook with a single API call

  // Fetch cities for filters based on selected state
  const fetchFilterCities = async (stateId) => {
    if (!stateId) {
      setFilterCities([]);
      return;
    }

    try {
      setLoadingFilterCities(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}cities?paginate=false&stateId=${stateId}`
      );
      setFilterCities(res.data || []);
    } catch (err) {
      console.error('Error fetching filter cities:', err);
    } finally {
      setLoadingFilterCities(false);
    }
  };

  // Use the branches data directly from API
  const currentData = branches;

  const handleSave = () => {
    if (editingBranch) {
      handleUpdateBranch();
    } else {
      handleAddBranch();
    }
  };

  const openEditModal = (branch) => {
    // console.log('Opening edit modal with branch:', branch);
    setEditingBranch(branch);
    setModalError(''); // Clear any previous errors

    const stateId = branch.state?.id || '';

    setNewBranch({
      first_name: branch.first_name || '',
      last_name: branch.last_name || '',
      email: branch.email || '',
      // Don't include password in edit mode to avoid changing it
      mobile_no: branch.mobile_no || '',
      address: branch.address || '',
      stateId: stateId,
      cityId: branch.city?.id || '',
      // Don't include ID in the form data, we'll add it during submission
    });

    // Fetch cities for the selected state
    if (stateId) {
      fetchCities(stateId);
    }

    setShowModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-10  text-gray-900">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8 lg:mb-10">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-blue-800 tracking-tight">
          🏢 Branch Management
        </h1>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          {role !== 'User' && (
            <button
              onClick={() => {
                setEditingBranch(null);
                setModalError('');
                setNewBranch({
                  first_name: '',
                  last_name: '',
                  email: '',
                  password: '12345678',
                  mobile_no: '',
                  address: '',
                  stateId: '',
                  cityId: '',
                });
                setShowModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base rounded-lg shadow-lg transition-all duration-200 font-semibold w-full sm:w-auto"
            >
              + Add Branch
            </button>
          )}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleExport}
            className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 transition text-white px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base rounded-lg shadow-lg w-full sm:w-auto font-semibold"
          >
            <Download size={16} className="flex-shrink-0" />
            <span>Export Excel</span>
          </motion.button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 sm:mb-8">
        <input
          type="text"
          placeholder="Search by Name or Email"
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          className="px-4 py-3 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={filters.state}
          onChange={(e) => handleFilterChange('state', e.target.value)}
          className="px-4 py-3 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 bg-gray-50 appearance-none"
          style={{
            backgroundImage:
              'url(\'data:image/svg+xml;charset=US-ASCII,<svg width="20" height="20" xmlns="http://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5z" fill="black"/></svg>\')',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 1rem center',
            paddingRight: '2.5rem',
          }}
        >
          <option value="">Select State</option>
          {loadingFilterStates ? (
            <option disabled>Loading states...</option>
          ) : (
            filterStates.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))
          )}
        </select>
        <select
          value={filters.city}
          onChange={(e) => handleFilterChange('city', e.target.value)}
          disabled={!filters.state || loadingFilterCities}
          className="px-4 py-3 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 bg-gray-50 appearance-none"
          style={{
            backgroundImage:
              'url(\'data:image/svg+xml;charset=US-ASCII,<svg width="20" height="20" xmlns="http://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5z" fill="black"/></svg>\')',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 1rem center',
            paddingRight: '2.5rem',
          }}
        >
          <option value="">Select City</option>
          {loadingFilterCities ? (
            <option disabled>Loading cities...</option>
          ) : filters.state ? (
            filterCities.map((city) => (
              <option key={city.id} value={city.id}>
                {city.name}
              </option>
            ))
          ) : (
            <option disabled>Select a state first</option>
          )}
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg">
          ⚠️ {error}
        </div>
      )}

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="overflow-hidden rounded-2xl shadow-xl border border-gray-100 bg-white"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                {[
                  '#',
                  'Branch Name',
                  'Email',
                  'Mobile',
                  'Address',
                  'State',
                  'City',
                  role !== 'User' && 'Actions',
                ].filter(Boolean).map((head) => (
                  <th
                    key={head}
                    className="py-4 px-6 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider"
                  >
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-10">
                    ⏳ Loading...
                  </td>
                </tr>
              ) : currentData.length > 0 ? (
                currentData.map((item, idx) => (
                  <motion.tr
                    key={item.id}
                    className="hover:bg-gray-50 transition-colors duration-200"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {idx + 1 + (currentPage - 1) * limit}
                    </td>
                    <td className="py-4 px-6 text-sm font-medium text-gray-900">
                      {item.first_name}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {item.email}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {item.mobile_no}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700 max-w-[220px] truncate">
                      <div className="flex items-center gap-2 group">
                        <span
                          className="truncate align-middle"
                          title={item.address}
                        >
                          {item.address || '-'}
                        </span>

                        {item.address && (
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(item.address);
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-gray-500 hover:text-blue-600 flex-shrink-0 align-middle"
                          >
                            <Copy size={16} />
                          </button>
                        )}
                      </div>
                    </td>

                    <td className="py-4 px-6 text-sm text-gray-700">
                      {item.state?.name || '-'}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {item.city?.name || '-'}
                    </td>
                    {role !== 'User' && (
                      <td className="py-4 px-6 text-sm">
                        <button
                          onClick={() => openEditModal(item)}
                          className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium rounded-lg shadow transition"
                        >
                          Edit
                        </button>
                      </td>
                    )}
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="text-center py-6 sm:py-8 md:py-10 text-gray-500 font-medium text-base sm:text-lg"
                  >
                    🚫 No branches found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 sm:mt-8 text-base sm:text-lg">
        <button
          disabled={currentPage === 1 || loading}
          onClick={() => setCurrentPage(currentPage - 1)}
          className="px-4 sm:px-5 py-2 bg-blue-200 hover:bg-blue-300 disabled:opacity-40 rounded-lg font-medium w-full sm:w-auto"
        >
          ⬅ Previous
        </button>
        <span className="text-gray-700 font-semibold order-first sm:order-none">
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          disabled={currentPage === totalPages || totalPages === 0 || loading}
          onClick={() => setCurrentPage(currentPage + 1)}
          className="px-4 sm:px-5 py-2 bg-blue-200 hover:bg-blue-300 disabled:opacity-40 rounded-lg font-medium w-full sm:w-auto"
        >
          Next ➡
        </button>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 md:p-10 w-[90%] sm:w-[80%] md:w-[700px] lg:w-[800px] max-h-[90vh] shadow-xl sm:shadow-2xl overflow-y-auto text-base sm:text-lg relative mx-4"
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{ zIndex: 1000 }}
            >
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6 md:mb-8 text-blue-700">
                {editingBranch ? '✏ Edit Branch' : '➕ Add Branch'}
              </h2>

              {/* Modal Error Display */}
              {modalError && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm sm:text-base">
                  ⚠️ {modalError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {[
                  { placeholder: 'Branch Name', key: 'first_name' },
                  { placeholder: 'Email', key: 'email', type: 'email' },
                  { placeholder: 'Mobile', key: 'mobile_no' },
                  { placeholder: 'Address', key: 'address' },
                ].map((field) => (
                  <input
                    key={field.key}
                    type={field.type || 'text'}
                    placeholder={field.placeholder}
                    value={newBranch[field.key]}
                    onChange={(e) =>
                      setNewBranch({
                        ...newBranch,
                        [field.key]: e.target.value,
                      })
                    }
                    className="border p-3 sm:p-4 rounded-lg text-base sm:text-lg shadow-sm focus:ring-2 focus:ring-blue-500"
                  />
                ))}

                {/* State Dropdown */}
                <select
                  value={newBranch.stateId}
                  onChange={handleStateChange}
                  className="border p-3 sm:p-4 rounded-lg text-base sm:text-lg shadow-sm focus:ring-2 focus:ring-blue-500 bg-gray-50 appearance-none"
                  style={{
                    backgroundImage:
                      'url(\'data:image/svg+xml;charset=US-ASCII,<svg width="20" height="20" xmlns="http://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5z" fill="black"/></svg>\')',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 1rem center',
                    paddingRight: '2.5rem',
                  }}
                >
                  <option value="">Select State</option>
                  {loadingStates ? (
                    <option disabled>Loading states...</option>
                  ) : (
                    states.map((state) => (
                      <option key={state.id} value={state.id}>
                        {state.name}
                      </option>
                    ))
                  )}
                </select>

                {/* City Dropdown */}
                <select
                  value={newBranch.cityId}
                  onChange={(e) =>
                    setNewBranch({
                      ...newBranch,
                      cityId: e.target.value,
                    })
                  }
                  disabled={!newBranch.stateId || loadingCities}
                  className="border p-3 sm:p-4 rounded-lg text-base sm:text-lg shadow-sm focus:ring-2 focus:ring-blue-500 bg-gray-50 appearance-none"
                  style={{
                    backgroundImage:
                      'url(\'data:image/svg+xml;charset=US-ASCII,<svg width="20" height="20" xmlns="http://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5z" fill="black"/></svg>\')',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 1rem center',
                    paddingRight: '2.5rem',
                  }}
                >
                  <option value="">Select City</option>
                  {loadingCities ? (
                    <option disabled>Loading cities...</option>
                  ) : newBranch.stateId ? (
                    cities.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>Select a state first</option>
                  )}
                </select>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-6 sm:mt-8 md:mt-10">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl bg-gray-300 hover:bg-gray-400 font-semibold order-2 sm:order-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg disabled:opacity-50 order-1 sm:order-2 mb-2 sm:mb-0"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
